import { useState, useEffect } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import Dialog from "@/src/components/atom/Dialog";
import DialogFooterContainer from "@/src/components/atom/Dialog/DialogFooterContainer";
import { Button } from "@/src/components/atom/Button";
import Typo from "@/src/components/atom/Typo";
import Checkbox from "@/src/components/atom/Checkbox";
import colorSet from "@/src/styles/color";
import {
  useCreateSubscriptionsMutation,
  useUpdateSubscriptionMutation,
  useCreatePaymentMethodsMutation,
  useUpdatePaymentMethodsMutation,
} from "@/src/store/apis/subscription";
import type {
  PlanDto,
  PlanVersionDto,
} from "@/src/store/apis/subscription/interface";
import useAlert from "@/src/hooks/useAlert";
import { useNavigate } from "react-router-dom";
import EXPORTER_PRIVATE_PATH from "@/src/routes/exporter/path";
import { useForm } from "react-hook-form";
import { useAppSelector } from "@/src/store";
import PaymentMethodForm, {
  PaymentFormValues,
  isFormValid,
} from "@/src/components/organism/PaymentMethodForm";
import CardInfoDisplay from "@/src/components/organism/CardInfoDisplay";
import { ReactComponent as FilterResetSvg } from "@/src/assets/icons/icon-filter-reset.svg";
import { PaymentMethodDto } from "@/src/store/apis/subscription/interface";

interface SubscriptionPlanModalProps {
  open: boolean;
  onOpenChange: (value: boolean) => void;
  plan: PlanDto;
  planVersion: PlanVersionDto;
  paymentCycle: "MONTHLY" | "ANNUALLY";
  paymentMethod?: PaymentMethodDto;
  isChangePlan?: boolean;
  currentSubscriptionId?: number;
}

function SubscriptionPlanModal({
  open,
  onOpenChange,
  plan,
  planVersion,
  paymentCycle,
  paymentMethod: propPaymentMethod,
  isChangePlan = false,
  currentSubscriptionId,
}: SubscriptionPlanModalProps) {
  const { t } = useTranslation([
    "subscriptionPlanSignup",
    "subscriptionManagement",
    "common",
  ]);

  const user = useAppSelector((state) => state.auth.user);

  const alert = useAlert();
  const navigate = useNavigate();

  const [isProcessing, setIsProcessing] = useState(false);
  const [isAgreed, setIsAgreed] = useState(false);
  const [isEditingPayment, setIsEditingPayment] = useState(false);

  const { control, watch, getValues, setValue, reset } =
    useForm<PaymentFormValues>({
      mode: "onBlur",
      defaultValues: {
        registerType: "BUSINESS",
        cardType: "CORPORATE_CARD",
        cardNumber: ["", "", "", ""],
        password2: "",
        expiredDate: "",
        userInfo: "",
      },
    });

  const [createSubscription] = useCreateSubscriptionsMutation();
  const [updateSubscription] = useUpdateSubscriptionMutation();
  const [createPaymentMethod] = useCreatePaymentMethodsMutation();
  const [updatePaymentMethod] = useUpdatePaymentMethodsMutation();

  const paymentMethod = isEditingPayment ? null : propPaymentMethod;

  // Reset form when modal is closed
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!open) {
      reset({
        registerType: "BUSINESS",
        cardType: "CORPORATE_CARD",
        cardNumber: ["", "", "", ""],
        password2: "",
        expiredDate: "",
        userInfo: "",
      });
      setIsAgreed(false);
      setIsEditingPayment(false);
      setIsProcessing(false);
    }
  }, [open]);

  const handleChangePaymentMethod = () => {
    setIsEditingPayment(true);
  };

  const isPaymentFormValid = () => {
    if (propPaymentMethod?.id && !isEditingPayment) {
      return true;
    }
    const formValues = getValues();
    return isFormValid(
      formValues.cardNumber || ["", "", "", ""],
      formValues.password2,
      formValues.expiredDate,
      formValues.registerType,
      formValues.cardType,
      formValues.userInfo,
    );
  };

  const handlePaymentMethod = async () => {
    // 기존 결제 수단이 있고 편집 모드가 아닌 경우
    if (propPaymentMethod?.id && !isEditingPayment) {
      return propPaymentMethod.id;
    }

    const formValues = getValues();
    const expiryDateYear = formValues.expiredDate.substring(3, 5);
    const expiryDateMonth = formValues.expiredDate.substring(0, 2);
    const userInfo =
      formValues.registerType === "BUSINESS" &&
      formValues.cardType === "CORPORATE_CARD"
        ? user?.exporter?.businessNumber || ""
        : formValues.userInfo || "";

    const paymentData = {
      cardNumber: formValues.cardNumber.join(""),
      expiryDate: `${expiryDateYear}${expiryDateMonth}`,
      password2: formValues.password2,
      userInfo,
      isRepresentative: true,
      exporterId: user!.exporter!.id,
    };

    // 기존 카드가 있고 편집 모드인 경우: 카드 수정
    if (propPaymentMethod?.id && isEditingPayment) {
      const result = await updatePaymentMethod({
        id: propPaymentMethod.id,
        ...paymentData,
      }).unwrap();
      return result.row?.id || propPaymentMethod.id;
    }

    // 카드가 없는 경우: 새 카드 등록
    const result = await createPaymentMethod(paymentData).unwrap();
    return result.row?.id;
  };

  const handlePlanChange = async () => {
    if (!currentSubscriptionId) {
      throw new Error("No current subscription ID for plan change");
    }

    // 기존 결제 수단이 있고 편집 모드가 아닌 경우
    if (propPaymentMethod?.id && !isEditingPayment) {
      // 기존 카드로 플랜 변경만 진행
      await updateSubscription({
        id: currentSubscriptionId,
        nextPlanId: plan.id,
        nextPlanPaymentCycle: paymentCycle,
      }).unwrap();
    } else {
      // 결제 수단 처리 후 플랜 변경
      await handlePaymentMethod();
      await updateSubscription({
        id: currentSubscriptionId,
        nextPlanId: plan.id,
        nextPlanPaymentCycle: paymentCycle,
      }).unwrap();
    }
  };

  const handleNewSubscription = async () => {
    // 기존 결제 수단이 있고 편집 모드가 아닌 경우
    if (propPaymentMethod?.id && !isEditingPayment) {
      await createSubscription({
        subscriptionStatus: "PROGRESS" as const,
        subscriptionType: "STANDARD" as const,
        planId: plan.id,
        planVersionId: planVersion.id,
        paymentCycle: paymentCycle,
        paymentMethodId: propPaymentMethod.id,
        exporterId: user!.exporter!.id,
      }).unwrap();
    } else if (propPaymentMethod?.id && isEditingPayment) {
      // 기존 카드 수정 후 구독
      const paymentMethodId = await handlePaymentMethod();
      await createSubscription({
        subscriptionStatus: "PROGRESS" as const,
        subscriptionType: "STANDARD" as const,
        planId: plan.id,
        planVersionId: planVersion.id,
        paymentCycle: paymentCycle,
        paymentMethodId,
        exporterId: user!.exporter!.id,
      }).unwrap();
    } else {
      // 새 카드로 구독 생성 (한 번의 API 호출)
      const formValues = getValues();
      const expiryDateYear = formValues.expiredDate.substring(3, 5);
      const expiryDateMonth = formValues.expiredDate.substring(0, 2);
      const userInfo =
        formValues.registerType === "BUSINESS" &&
        formValues.cardType === "CORPORATE_CARD"
          ? user?.exporter?.businessNumber || ""
          : formValues.userInfo || "";

      await createSubscription({
        subscriptionStatus: "PROGRESS" as const,
        subscriptionType: "STANDARD" as const,
        planId: plan.id,
        planVersionId: planVersion.id,
        paymentCycle: paymentCycle,
        cardNumber: formValues.cardNumber.join(""),
        expiryDate: `${expiryDateYear}${expiryDateMonth}`,
        password2: formValues.password2,
        userInfo,
        isRepresentative: true,
        exporterId: user!.exporter!.id,
      }).unwrap();
    }
  };

  const handleSubscribe = async () => {
    // 유효성 검사
    if (!isPaymentFormValid()) {
      alert.showAlert({
        type: "warning" as const,
        message: t("subscriptionPlanSignup:paymentInfoTitle"),
      });
      return;
    }

    if (!user?.exporter?.id) {
      alert.showAlert({
        type: "error",
        message: t("error:userNotFound"),
      });
      return;
    }

    if (!plan?.id || !planVersion?.id) {
      alert.showAlert({
        type: "error",
        message: t("subscriptionPlanSignup:invalidPlanInfo"),
      });
      return;
    }

    setIsProcessing(true);

    try {
      // 플랜 변경 또는 신규 구독 처리
      if (isChangePlan && currentSubscriptionId) {
        await handlePlanChange();
      } else {
        await handleNewSubscription();
      }

      // 성공 메시지
      alert.showAlert({
        type: "success",
        message: isChangePlan
          ? t("subscriptionManagement:subscription.alert.planChangeCompleted")
          : t("subscriptionPlanSignup:planSubscriptionCompleted"),
      });

      setIsEditingPayment(false);
      onOpenChange(false);
      navigate(EXPORTER_PRIVATE_PATH.SUBSCRIPTION_MANAGEMENT);
    } catch (error: any) {
      // 에러 로깅
      console.error("Subscription operation failed:", {
        error,
        operation: isChangePlan ? "plan-change" : "new-subscription",
        hasExistingPayment: !!propPaymentMethod,
        isEditingPayment,
      });

      // 사용자 에러 메시지
      const userMessage = isChangePlan
        ? t("subscriptionPlanSignup:changePlanFailed")
        : t("subscriptionPlanSignup:subscribeFailed");

      alert.showAlert({
        type: "error",
        message: userMessage,
      });
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Dialog
      title={
        isChangePlan
          ? t("subscriptionManagement:subscription.button.planChange")
          : t("subscriptionPlanSignup:title")
      }
      open={open}
      onOpenChange={onOpenChange}
      width={720}
      isNotPadding
      beforeFooter={
        <CheckboxSection>
          <CheckboxContainer>
            <CheckboxWrapper>
              <Checkbox
                checked={isAgreed}
                onChange={(e) => setIsAgreed(e.target.checked)}
              />
              <Typo typoType="b7r" color="gray2">
                {t("subscriptionPlanSignup:agreementText")}
              </Typo>
            </CheckboxWrapper>
          </CheckboxContainer>
        </CheckboxSection>
      }
      footer={
        <DialogFooterContainer>
          <Button
            buttonGrade="tertiary"
            buttonColor="black"
            onClick={() => onOpenChange(false)}
            disabled={isProcessing}
          >
            {t("common:cancel")}
          </Button>
          <Button
            buttonGrade="primary"
            buttonColor="blue"
            onClick={handleSubscribe}
            disabled={!isPaymentFormValid() || !isAgreed || isProcessing}
            isLoading={isProcessing}
          >
            {t("common:subscription")}
          </Button>
        </DialogFooterContainer>
      }
    >
      <Container>
        <Section>
          <SectionTitle>{t("subscriptionPlanSignup:planTitle")}</SectionTitle>
          <PlanCard>
            <PlanMainInfo>
              <PlanName>{plan.name || plan.planCode}</PlanName>
              <PriceContainer>
                <MainPriceRow>
                  <MainPrice>
                    {Number(
                      paymentCycle === "MONTHLY"
                        ? planVersion.monthlyFee
                        : planVersion.annualFee,
                    ).toLocaleString()}{" "}
                    {planVersion.currencyUnit}
                  </MainPrice>
                  <PriceUnit>
                    /
                    {paymentCycle === "MONTHLY"
                      ? t("subscriptionPlanSignup:month")
                      : t("subscriptionPlanSignup:year")}
                  </PriceUnit>
                </MainPriceRow>
                {paymentCycle === "ANNUALLY" && planVersion.monthlyFee && (
                  <OriginalPrice>
                    {Number(planVersion.monthlyFee).toLocaleString()}{" "}
                    {planVersion.currencyUnit}/month
                  </OriginalPrice>
                )}
              </PriceContainer>
            </PlanMainInfo>
            <PlanDetailsContainer>
              <PlanDetailItem>
                <DetailLabel>
                  {t("subscriptionPlanSignup:usedAccount")}
                </DetailLabel>
                <DetailValue>{planVersion.numberOfMember || "-"}</DetailValue>
              </PlanDetailItem>
              <PlanDetailItem>
                <DetailLabel>
                  {t("subscriptionPlanSignup:loadingCode")}
                </DetailLabel>
                <DetailValue>
                  {planVersion.numberOfLoadingCode || "-"}
                </DetailValue>
              </PlanDetailItem>
            </PlanDetailsContainer>
          </PlanCard>
        </Section>

        <Section>
          <SectionHeaderWithButton>
            <SectionTitle>
              {t("subscriptionPlanSignup:paymentMethodSectionTitle")}
            </SectionTitle>
            {propPaymentMethod && (
              <ChangeButton
                buttonGrade="tertiary"
                buttonColor="black"
                buttonSize={32}
                onClick={handleChangePaymentMethod}
              >
                <FilterResetSvg />
                {t("subscriptionManagement:subscription.button.change")}
              </ChangeButton>
            )}
          </SectionHeaderWithButton>
          <PaymentFormCard
            $hasPaymentMethod={!!paymentMethod && !isEditingPayment}
          >
            {paymentMethod && !isEditingPayment ? (
              <CardInfoDisplay
                payment={paymentMethod}
                isBusiness={
                  user?.exporter?.businessNumber === paymentMethod.userInfo
                }
              />
            ) : (
              <PaymentMethodForm
                control={control}
                watch={watch}
                setValue={setValue}
                getValues={getValues}
                businessNumber={user?.exporter?.businessNumber || ""}
              />
            )}
          </PaymentFormCard>
        </Section>
      </Container>
    </Dialog>
  );
}

export default SubscriptionPlanModal;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 24px;
  flex: 1 1 auto;
  max-height: calc(100vh - 320px);
  overflow-y: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SectionTitle = styled(Typo).attrs({
  typoType: "h7",
})`
  color: ${colorSet.gray2};
  font-weight: 700;
`;

const PlanCard = styled.div`
  padding: 20px;
  border: 1px solid ${colorSet.gray9};
  border-radius: 8px;
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: flex-start;
  gap: 8px;
  background: ${colorSet.white};
`;

const PlanName = styled(Typo).attrs({
  typoType: "h5",
})`
  color: ${colorSet.blue4};
  font-weight: 700;
  font-size: 20px;
  line-height: 30px;
`;

const PlanMainInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: flex-start;
  flex: 1;
`;

const PriceContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: flex-start;
`;

const MainPriceRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const MainPrice = styled.div`
  font-family: "Pretendard";
  font-weight: 700;
  font-size: 28px;
  line-height: 38px;
  color: ${colorSet.gray2};
`;

const PriceUnit = styled.div`
  font-family: "Pretendard";
  font-weight: 500;
  font-size: 16px;
  line-height: 24px;
  color: ${colorSet.gray6};
`;

const OriginalPrice = styled.div`
  font-family: "Pretendard";
  font-weight: 500;
  font-size: 16px;
  line-height: 24px;
  color: ${colorSet.gray7};
  text-decoration: line-through;
`;

const PlanDetailsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  align-items: flex-start;
  flex: 1;
`;

const PlanDetailItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: flex-start;
`;

const DetailLabel = styled.div`
  font-family: "Pretendard";
  font-weight: 500;
  font-size: 14px;
  line-height: 18px;
  color: ${colorSet.gray5};
  white-space: nowrap;
`;

const DetailValue = styled.div`
  font-family: "Pretendard";
  font-weight: 500;
  font-size: 19px;
  line-height: 26px;
  color: ${colorSet.gray2};
`;

const PaymentFormCard = styled.div<{ $hasPaymentMethod?: boolean }>`
  ${({ $hasPaymentMethod }) =>
    !$hasPaymentMethod &&
    `
    padding: 20px;
    border: 1px solid ${colorSet.gray9};
    border-radius: 8px;
  `}
`;

const CheckboxSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 9px;
  align-items: flex-start;
  padding: 16px 24px 0 24px;
  width: 100%;
`;

const CheckboxContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 9px;
  align-items: flex-start;
  width: 100%;
`;

const CheckboxWrapper = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const SectionHeaderWithButton = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

const ChangeButton = styled(Button)`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  height: 32px;
  border: 1px solid ${colorSet.gray9};
  border-radius: 8px;
  background-color: ${colorSet.white};
  box-shadow:
    0px 0px 0px 0.5px rgba(160, 164, 171, 0.2),
    0px 1px 2px 0px rgba(5, 29, 57, 0.1);

  svg {
    width: 16px;
    height: 16px;

    path {
      fill: ${colorSet.gray2};
    }
  }
`;
